import type { GpxPoint } from './parseGpx';

export type ElevationPoint = { lat: number; lon: number; ele?: number };

export type ElevationStats = {
  totalAscent: number;
  totalDescent: number;
  minElevation: number;
  maxElevation: number;
  steepestGrade: number;
  cumulativeDistance: number;
};

const EARTH_RADIUS_M = 6_371_000;
const NOISE_THRESHOLD_M = 1;
const ELEVATION_BATCH_SIZE = 100;

function toRad(degrees: number): number {
  return degrees * Math.PI / 180;
}

export function distanceMeters(a: ElevationPoint, b: ElevationPoint): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const haversine = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(Math.min(1, haversine)));
}

export function thinElevationPoints(points: GpxPoint[], maxPoints = 100): GpxPoint[] {
  if (points.length <= maxPoints) return points;
  if (maxPoints < 2) return points.slice(0, maxPoints);
  return Array.from({ length: maxPoints }, (_, index) => points[Math.round(index * (points.length - 1) / (maxPoints - 1))]!);
}

/** Fetches at most 100 coordinates per free Open-Meteo elevation request. */
export async function fetchElevation(
  points: ElevationPoint[],
  fetchImpl: typeof fetch = fetch,
): Promise<number[]> {
  const result: number[] = [];
  for (let start = 0; start < points.length; start += ELEVATION_BATCH_SIZE) {
    const batch = points.slice(start, start + ELEVATION_BATCH_SIZE);
    const url = new URL('https://api.open-meteo.com/v1/elevation');
    url.searchParams.set('latitude', batch.map((point) => point.lat.toFixed(5)).join(','));
    url.searchParams.set('longitude', batch.map((point) => point.lon.toFixed(5)).join(','));
    const response = await fetchImpl(url.toString());
    if (!response.ok) throw new Error(`Elevation HTTP ${response.status}`);
    const data = await response.json() as { elevation?: unknown };
    const elevations = Array.isArray(data.elevation) ? data.elevation : [];
    for (let index = 0; index < batch.length; index += 1) {
      const elevation = Number(elevations[index]);
      result.push(Number.isFinite(elevation) ? elevation : NaN);
    }
  }
  return result;
}

export function withFetchedElevations(points: GpxPoint[], elevations: number[]): GpxPoint[] {
  return points.map((point, index) => {
    const elevation = elevations[index];
    return Number.isFinite(elevation) ? { ...point, ele: elevation } : point;
  });
}

export function computeElevationStats(points: ElevationPoint[]): ElevationStats {
  if (!points.length) {
    return { totalAscent: 0, totalDescent: 0, minElevation: 0, maxElevation: 0, steepestGrade: 0, cumulativeDistance: 0 };
  }
  let totalAscent = 0;
  let totalDescent = 0;
  let minElevation = Infinity;
  let maxElevation = -Infinity;
  let steepestGrade = 0;
  let cumulativeDistance = 0;
  const rangePoints = points;
  for (const point of rangePoints) {
    if (point.ele != null && Number.isFinite(point.ele)) {
      minElevation = Math.min(minElevation, point.ele);
      maxElevation = Math.max(maxElevation, point.ele);
    }
  }
  let stableElevation = points[0]?.ele;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]!;
    const current = points[index]!;
    const distance = distanceMeters(previous, current);
    cumulativeDistance += distance;
    if (previous.ele == null || current.ele == null || !Number.isFinite(previous.ele) || !Number.isFinite(current.ele)) continue;
    const rawDelta = current.ele - (stableElevation ?? previous.ele);
    const delta = Math.abs(rawDelta) <= NOISE_THRESHOLD_M ? 0 : rawDelta;
    if (delta !== 0) stableElevation = current.ele;
    if (delta > NOISE_THRESHOLD_M) totalAscent += delta;
    else if (delta < -NOISE_THRESHOLD_M) totalDescent += Math.abs(delta);
    if (distance > 0) {
      const grade = delta / distance * 100;
      if (Math.abs(grade) > Math.abs(steepestGrade)) steepestGrade = grade;
    }
  }
  return {
    totalAscent,
    totalDescent,
    minElevation: Number.isFinite(minElevation) ? minElevation : 0,
    maxElevation: Number.isFinite(maxElevation) ? maxElevation : 0,
    steepestGrade,
    cumulativeDistance,
  };
}
